import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import "@shoelace-style/shoelace/dist/components/card/card.js";
import "@shoelace-style/shoelace/dist/components/radio-group/radio-group.js";
import "@shoelace-style/shoelace/dist/components/radio-button/radio-button.js";
import "@shoelace-style/shoelace/dist/components/select/select.js";
import "@shoelace-style/shoelace/dist/components/option/option.js";
import "@shoelace-style/shoelace/dist/components/input/input.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/divider/divider.js";
import "@shoelace-style/shoelace/dist/components/alert/alert.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/dialog/dialog.js";
import "../components/app-shell";
import { getDb, saveSettings } from "../db";
import { syncService } from "../services/sync";
import type {
  UserSettings,
  DoseIncrement,
  MealTimes,
  DataSharingLevel,
  TargetProfileType,
  CorrectionStackingMode,
} from "../types";
import {
  InsulinDelivery,
  QAInsulinType,
  BIInsulinType,
  BIFrequency,
  QA_INSULIN_NAMES,
  BI_INSULIN_NAMES,
  DOSE_INCREMENT_INFO,
  CORRECTION_STACKING_INFO,
  DEFAULT_SETTINGS,
  DEFAULT_MEAL_TIMES,
  DATA_SHARING_LABELS,
} from "../types";
import {
  TARGET_PROFILES,
  unitsPerCPToGramsPerUnit,
  getUnitsPerCPFromSettings,
} from "../config/dafne";

@customElement("settings-page")
export class SettingsPage extends LitElement {
  @state() private settings: UserSettings = { ...DEFAULT_SETTINGS };
  @state() private saving = false;
  @state() private saved = false;
  @state() private syncing = false;
  @state() private syncMessage = "";
  @state() private showDeleteDialog = false;
  @state() private deleteConfirmText = "";
  @state() private deleting = false;
  @state() private deleteError = "";

  static styles = css`
    :host {
      display: block;
    }

    .settings-content {
      padding: 1rem;
      max-width: 600px;
      margin: 0 auto;
    }

    h2 {
      font-size: 1.25rem;
      font-weight: 600;
      margin: 1.5rem 0 1rem;
      color: #212529;
    }

    h2:first-of-type {
      margin-top: 0;
    }

    .setting-row {
      margin-bottom: 1.25rem;
    }

    .setting-row label {
      display: block;
      font-weight: 500;
      margin-bottom: 0.5rem;
      color: #495057;
    }

    .setting-row .hint {
      font-size: 0.75rem;
      color: #6c757d;
      margin-top: 0.25rem;
    }

    .ratio-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      gap: 1rem;
    }

    .ratio-grid.meal-times {
      grid-template-columns: 1fr;
      max-width: 300px;
    }

    .ratio-grid.meal-times .ratio-item {
      flex-direction: row;
      align-items: center;
      gap: 1rem;
    }

    .ratio-grid.meal-times .ratio-item label {
      min-width: 80px;
      margin-bottom: 0;
    }

    .ratio-grid.meal-times .ratio-item sl-input {
      flex: 1;
    }

    .ratio-item {
      display: flex;
      flex-direction: column;
    }

    .ratio-item label {
      font-size: 0.75rem;
      font-weight: 500;
      color: #6c757d;
      margin-bottom: 0.25rem;
    }

    sl-card {
      margin-bottom: 1rem;
    }

    sl-card::part(body) {
      padding: 1rem;
    }

    .period-name {
      font-weight: 500;
      margin-bottom: 0.75rem;
      color: #212529;
    }

    sl-divider {
      --spacing: 1.5rem;
    }

    .actions {
      display: flex;
      gap: 1rem;
      margin-top: 2rem;
      padding-bottom: 2rem;
    }

    sl-alert {
      margin-bottom: 1rem;
    }

    .target-ranges-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.875rem;
      margin-top: 1rem;
    }

    .target-ranges-table th,
    .target-ranges-table td {
      padding: 0.5rem;
      text-align: left;
      border-bottom: 1px solid #dee2e6;
    }

    .target-ranges-table th {
      font-weight: 600;
      color: #495057;
      background: #f8f9fa;
    }

    .target-ranges-table td {
      color: #212529;
    }

    .target-ranges-table .range-value {
      font-family: monospace;
      color: #198754;
    }

    .profile-description {
      font-size: 0.875rem;
      color: #6c757d;
      margin-top: 0.5rem;
      font-style: italic;
    }

    .danger-zone {
      margin-top: 3rem;
      padding-top: 1.5rem;
      border-top: 2px solid #dc3545;
    }

    .danger-zone h2 {
      color: #dc3545;
    }

    .danger-zone-card {
      background: #fff5f5;
      border: 1px solid #dc3545;
      border-radius: 0.5rem;
      padding: 1rem;
    }

    .danger-zone-card h3 {
      margin: 0 0 0.5rem 0;
      font-size: 1rem;
      font-weight: 600;
      color: #dc3545;
    }

    .danger-zone-card p {
      margin: 0 0 1rem 0;
      font-size: 0.875rem;
      color: #495057;
    }

    .delete-confirm-input {
      margin-top: 1rem;
    }

    .delete-confirm-input label {
      display: block;
      font-size: 0.875rem;
      color: #495057;
      margin-bottom: 0.5rem;
    }

    .delete-confirm-input code {
      background: #f1f1f1;
      padding: 0.125rem 0.375rem;
      border-radius: 0.25rem;
      font-family: monospace;
      color: #dc3545;
    }

    /* Tablet+ layout */
    @media (min-width: 768px) {
      .settings-content {
        padding: 1.5rem;
      }

      .ratio-grid {
        grid-template-columns: repeat(3, 1fr);
      }
    }
  `;

  async connectedCallback() {
    super.connectedCallback();
    await this._loadSettings();
  }

  private async _loadSettings() {
    try {
      const stored = await getDb().settings.get("settings");
      if (stored) {
        this.settings = stored;
      }
    } catch (error) {
      console.error("Failed to load settings:", error);
    }
  }

  private async _saveSettings() {
    this.saving = true;
    try {
      // Use saveSettings from operations.ts to ensure sync metadata is set
      await saveSettings(this.settings);
      this.saved = true;
      setTimeout(() => {
        this.saved = false;
      }, 3000);
    } catch (error) {
      console.error("Failed to save settings:", error);
    } finally {
      this.saving = false;
    }
  }

  private async _forceSync() {
    this.syncing = true;
    this.syncMessage = "";
    try {
      const result = await syncService.checkAndSync();
      if (result.success) {
        this.syncMessage = `Sync complete. Pushed: ${result.pushed || 0}, Pulled: ${result.pulled || 0}`;
      } else {
        this.syncMessage = result.error || "Sync failed";
      }
      setTimeout(() => {
        this.syncMessage = "";
      }, 5000);
    } catch (error) {
      console.error("Sync failed:", error);
      this.syncMessage = error instanceof Error ? error.message : "Sync failed";
    } finally {
      this.syncing = false;
    }
  }

  private async _forcePushAll() {
    this.syncing = true;
    this.syncMessage = "Pushing all data to server...";
    try {
      const result = await syncService.forcePushAll();
      if (result.success) {
        this.syncMessage = `Force push complete. Pushed: ${result.pushed || 0} items`;
      } else {
        this.syncMessage = result.error || "Force push failed";
      }
      setTimeout(() => {
        this.syncMessage = "";
      }, 5000);
    } catch (error) {
      console.error("Force push failed:", error);
      this.syncMessage =
        error instanceof Error ? error.message : "Force push failed";
    } finally {
      this.syncing = false;
    }
  }

  private _updateSetting<K extends keyof UserSettings>(
    key: K,
    value: UserSettings[K],
  ) {
    this.settings = { ...this.settings, [key]: value };
  }

  private _updateCPRatio(period: string, unitsPerCP: number) {
    // Use centralized conversion from config/dafne/insulin.ts
    const gramsPerUnit = unitsPerCPToGramsPerUnit(unitsPerCP);
    this.settings = {
      ...this.settings,
      cpRatios: { ...this.settings.cpRatios, [period]: gramsPerUnit },
    };
  }

  private _updateMealTime(key: keyof MealTimes, value: string) {
    const currentMealTimes = this.settings.mealTimes || {
      ...DEFAULT_MEAL_TIMES,
    };
    this.settings = {
      ...this.settings,
      mealTimes: { ...currentMealTimes, [key]: value },
    };
  }

  private _updateDataSharing(
    field: "allowDafneDataSharing" | "allowCgmDataSharing",
    value: DataSharingLevel,
  ) {
    const currentDataSharing = this.settings.dataSharing || {
      allowDafneDataSharing: "none" as DataSharingLevel,
      allowCgmDataSharing: "none" as DataSharingLevel,
    };
    this.settings = {
      ...this.settings,
      dataSharing: {
        ...currentDataSharing,
        [field]: value,
        consentUpdatedAt: new Date(),
      },
    };
  }

  // Use centralized conversion from config/dafne/insulin.ts
  private _getUnitsPerCP(period: string): number {
    return getUnitsPerCPFromSettings(this.settings.cpRatios, period);
  }

  private _openDeleteDialog() {
    this.deleteConfirmText = "";
    this.deleteError = "";
    this.showDeleteDialog = true;
  }

  private _closeDeleteDialog() {
    this.showDeleteDialog = false;
    this.deleteConfirmText = "";
    this.deleteError = "";
  }

  private async _confirmDeleteAllData() {
    const CONFIRM_PHRASE = "DELETE ALL MY DATA";

    if (this.deleteConfirmText !== CONFIRM_PHRASE) {
      this.deleteError = `Please type "${CONFIRM_PHRASE}" exactly to confirm`;
      return;
    }

    this.deleting = true;
    this.deleteError = "";

    try {
      const result = await syncService.deleteAllData();

      if (result.success) {
        this._closeDeleteDialog();
        // Redirect to login after data deletion
        window.location.href = "/login";
      } else {
        this.deleteError = result.error || "Failed to delete data";
      }
    } catch (error) {
      this.deleteError =
        error instanceof Error ? error.message : "Unknown error";
    } finally {
      this.deleting = false;
    }
  }

  private _renderTargetRangesTable() {
    const profile = TARGET_PROFILES[this.settings.targetProfile || "standard"];
    return html`
      <table class="target-ranges-table">
        <thead>
          <tr>
            <th>Time Period</th>
            <th>Target Range (mmol/L)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Before Breakfast (fasting)</td>
            <td class="range-value">
              ${profile.preMeal.B.low} - ${profile.preMeal.B.high}
            </td>
          </tr>
          <tr>
            <td>Before Lunch</td>
            <td class="range-value">
              ${profile.preMeal.L.low} - ${profile.preMeal.L.high}
            </td>
          </tr>
          <tr>
            <td>Before Evening Meal</td>
            <td class="range-value">
              ${profile.preMeal.EM.low} - ${profile.preMeal.EM.high}
            </td>
          </tr>
          <tr>
            <td>Before Bed</td>
            <td class="range-value">
              ${profile.preMeal.BT.low} - ${profile.preMeal.BT.high}
            </td>
          </tr>
          <tr>
            <td>3AM Check (overnight)</td>
            <td class="range-value">
              ${profile.preMeal["3AM"].low} - ${profile.preMeal["3AM"].high}
            </td>
          </tr>
          <tr>
            <td>2 Hours After Meals</td>
            <td class="range-value">
              ${profile.postMeal.low} - ${profile.postMeal.high}
            </td>
          </tr>
        </tbody>
      </table>
    `;
  }

  render() {
    return html`
      <app-shell pageTitle="Settings">
        <div class="settings-content">
          <h2>Insulin Delivery</h2>

          <div class="setting-row">
            <label>Delivery Method</label>
            <sl-radio-group
              value=${this.settings.insulinDelivery || "mdi"}
              @sl-change=${(e: Event) =>
                this._updateSetting(
                  "insulinDelivery",
                  (e.target as HTMLInputElement).value as InsulinDelivery,
                )}
            >
              <sl-radio-button value="mdi">MDI (Injections)</sl-radio-button>
              <sl-radio-button value="pump">Insulin Pump</sl-radio-button>
            </sl-radio-group>
          </div>

          <div class="setting-row">
            <label>Minimum Dose Increment</label>
            <sl-select
              value=${String(this.settings.doseIncrement || 0.5)}
              @sl-change=${(e: Event) =>
                this._updateSetting(
                  "doseIncrement",
                  Number(
                    (e.target as HTMLSelectElement).value,
                  ) as DoseIncrement,
                )}
            >
              ${(
                Object.keys(DOSE_INCREMENT_INFO) as unknown as DoseIncrement[]
              ).map(
                (value) => html`
                  <sl-option value=${String(value)}>
                    ${DOSE_INCREMENT_INFO[value].label} -
                    ${DOSE_INCREMENT_INFO[value].description}
                  </sl-option>
                `,
              )}
            </sl-select>
            <div class="hint">
              The smallest dose your pen or pump can deliver. Recommendations
              will be rounded to this increment.
            </div>
          </div>

          <sl-divider></sl-divider>

          <h2>Quick-Acting Insulin (QA)</h2>

          <div class="setting-row">
            <label>QA Insulin Type</label>
            <sl-select
              value=${this.settings.qaInsulinType}
              @sl-change=${(e: Event) =>
                this._updateSetting(
                  "qaInsulinType",
                  (e.target as HTMLSelectElement).value as QAInsulinType,
                )}
            >
              ${Object.entries(QA_INSULIN_NAMES).map(
                ([value, label]) => html`
                  <sl-option value=${value}>${label}</sl-option>
                `,
              )}
            </sl-select>
            <div class="hint">Taken with meals to cover carbs eaten</div>
          </div>

          <sl-divider></sl-divider>

          <h2>Background Insulin (BI)</h2>

          <div class="setting-row">
            <label>BI Insulin Type</label>
            <sl-select
              value=${this.settings.biInsulinType}
              @sl-change=${(e: Event) =>
                this._updateSetting(
                  "biInsulinType",
                  (e.target as HTMLSelectElement).value as BIInsulinType,
                )}
            >
              ${Object.entries(BI_INSULIN_NAMES).map(
                ([value, label]) => html`
                  <sl-option value=${value}>${label}</sl-option>
                `,
              )}
            </sl-select>
          </div>

          <div class="setting-row">
            <label>BI Frequency</label>
            <sl-radio-group
              value=${this.settings.biFrequency}
              @sl-change=${(e: Event) =>
                this._updateSetting(
                  "biFrequency",
                  (e.target as HTMLInputElement).value as BIFrequency,
                )}
            >
              <sl-radio-button value="once">Once Daily</sl-radio-button>
              <sl-radio-button value="twice">Twice Daily</sl-radio-button>
            </sl-radio-group>
            <div class="hint">
              NPH insulins (Protaphane, Humulin NPH) typically require twice
              daily dosing
            </div>
          </div>

          <sl-divider></sl-divider>

          <h2>CP Ratios</h2>
          <p class="hint" style="margin-bottom: 1rem;">
            Units per CP (10g carbs). Example: 1 means 1 unit for every 10g
            carbs. 3 means 3 units for every 10g carbs (tighter control).
          </p>

          <div class="ratio-grid">
            <div class="ratio-item">
              <label>Breakfast</label>
              <sl-input
                type="number"
                min="0.5"
                max="10"
                step="0.5"
                value=${this._getUnitsPerCP("B")}
                @sl-change=${(e: Event) =>
                  this._updateCPRatio(
                    "B",
                    Number((e.target as HTMLInputElement).value),
                  )}
              ></sl-input>
            </div>
            <div class="ratio-item">
              <label>Lunch</label>
              <sl-input
                type="number"
                min="0.5"
                max="10"
                step="0.5"
                value=${this._getUnitsPerCP("L")}
                @sl-change=${(e: Event) =>
                  this._updateCPRatio(
                    "L",
                    Number((e.target as HTMLInputElement).value),
                  )}
              ></sl-input>
            </div>
            <div class="ratio-item">
              <label>Evening Meal</label>
              <sl-input
                type="number"
                min="0.5"
                max="10"
                step="0.5"
                value=${this._getUnitsPerCP("EM")}
                @sl-change=${(e: Event) =>
                  this._updateCPRatio(
                    "EM",
                    Number((e.target as HTMLInputElement).value),
                  )}
              ></sl-input>
            </div>
          </div>

          <sl-divider></sl-divider>

          <h2>Correction Factor</h2>

          <div class="setting-row">
            <label>ISF (Insulin Sensitivity Factor)</label>
            <sl-input
              type="number"
              min="0.5"
              max="10"
              step="0.1"
              value=${this.settings.correctionFactor || ""}
              placeholder="e.g., 2.0"
              @sl-change=${(e: Event) =>
                this._updateSetting(
                  "correctionFactor",
                  Number((e.target as HTMLInputElement).value),
                )}
            ></sl-input>
            <div class="hint">
              How much 1 unit of QA lowers your BG (in mmol/L). Example: 2.0
              means 1u drops BG by 2.0 mmol/L.
            </div>
          </div>

          <div class="setting-row">
            <label>IOB / Stacking Behaviour</label>
            <sl-radio-group
              value=${this.settings.correctionStacking || "no-stacking"}
              @sl-change=${(e: Event) =>
                this._updateSetting(
                  "correctionStacking",
                  (e.target as HTMLInputElement)
                    .value as CorrectionStackingMode,
                )}
            >
              ${Object.entries(CORRECTION_STACKING_INFO).map(
                ([value, info]) => html`
                  <sl-radio-button value=${value}
                    >${info.label}</sl-radio-button
                  >
                `,
              )}
            </sl-radio-group>
            <div class="hint">
              ${CORRECTION_STACKING_INFO[
                this.settings.correctionStacking || "no-stacking"
              ].description}
            </div>
          </div>

          <sl-divider></sl-divider>

          <h2>Target Profile</h2>

          <div class="setting-row">
            <label>Which target range profile should you use?</label>
            <sl-radio-group
              value=${this.settings.targetProfile || "standard"}
              @sl-change=${(e: Event) =>
                this._updateSetting(
                  "targetProfile",
                  (e.target as HTMLInputElement).value as TargetProfileType,
                )}
            >
              <sl-radio-button value="standard">Standard</sl-radio-button>
              <sl-radio-button value="upper">Upper</sl-radio-button>
            </sl-radio-group>
            <div class="profile-description">
              ${TARGET_PROFILES[this.settings.targetProfile || "standard"]
                .description}
            </div>
          </div>

          ${this._renderTargetRangesTable()}

          <sl-divider></sl-divider>

          <h2>Typical Meal Times</h2>

          <div class="setting-row">
            <div class="hint" style="margin-bottom: 0.75rem;">
              Your typical schedule helps suggest the right period when adding
              entries.
            </div>
            <div class="ratio-grid meal-times">
              <div class="ratio-item">
                <label>Wake Up</label>
                <sl-input
                  type="time"
                  value=${this.settings.mealTimes?.wakeUp ||
                  DEFAULT_MEAL_TIMES.wakeUp}
                  @sl-change=${(e: Event) =>
                    this._updateMealTime(
                      "wakeUp",
                      (e.target as HTMLInputElement).value,
                    )}
                ></sl-input>
              </div>
              <div class="ratio-item">
                <label>Breakfast</label>
                <sl-input
                  type="time"
                  value=${this.settings.mealTimes?.breakfast ||
                  DEFAULT_MEAL_TIMES.breakfast}
                  @sl-change=${(e: Event) =>
                    this._updateMealTime(
                      "breakfast",
                      (e.target as HTMLInputElement).value,
                    )}
                ></sl-input>
              </div>
              <div class="ratio-item">
                <label>Lunch</label>
                <sl-input
                  type="time"
                  value=${this.settings.mealTimes?.lunch ||
                  DEFAULT_MEAL_TIMES.lunch}
                  @sl-change=${(e: Event) =>
                    this._updateMealTime(
                      "lunch",
                      (e.target as HTMLInputElement).value,
                    )}
                ></sl-input>
              </div>
              <div class="ratio-item">
                <label>Dinner</label>
                <sl-input
                  type="time"
                  value=${this.settings.mealTimes?.dinner ||
                  DEFAULT_MEAL_TIMES.dinner}
                  @sl-change=${(e: Event) =>
                    this._updateMealTime(
                      "dinner",
                      (e.target as HTMLInputElement).value,
                    )}
                ></sl-input>
              </div>
              <div class="ratio-item">
                <label>Bedtime</label>
                <sl-input
                  type="time"
                  value=${this.settings.mealTimes?.bedtime ||
                  DEFAULT_MEAL_TIMES.bedtime}
                  @sl-change=${(e: Event) =>
                    this._updateMealTime(
                      "bedtime",
                      (e.target as HTMLInputElement).value,
                    )}
                ></sl-input>
              </div>
            </div>
          </div>

          <sl-divider></sl-divider>

          <h2>Data Sharing</h2>
          <p class="hint" style="margin-bottom: 1rem;">
            Choose how your data may be used by your clinic for training and
            support purposes.
          </p>

          <div class="setting-row">
            <label>DAFNE Log Data</label>
            <sl-radio-group
              value=${this.settings.dataSharing?.allowDafneDataSharing ||
              "none"}
              @sl-change=${(e: Event) =>
                this._updateDataSharing(
                  "allowDafneDataSharing",
                  (e.target as HTMLInputElement).value as DataSharingLevel,
                )}
            >
              ${Object.entries(DATA_SHARING_LABELS).map(
                ([value, info]) => html`
                  <sl-radio-button value=${value}
                    >${info.label}</sl-radio-button
                  >
                `,
              )}
            </sl-radio-group>
            <div class="hint">
              ${DATA_SHARING_LABELS[
                this.settings.dataSharing?.allowDafneDataSharing || "none"
              ].description}
            </div>
          </div>

          <div class="setting-row">
            <label>CGM Data</label>
            <sl-radio-group
              value=${this.settings.dataSharing?.allowCgmDataSharing || "none"}
              @sl-change=${(e: Event) =>
                this._updateDataSharing(
                  "allowCgmDataSharing",
                  (e.target as HTMLInputElement).value as DataSharingLevel,
                )}
            >
              ${Object.entries(DATA_SHARING_LABELS).map(
                ([value, info]) => html`
                  <sl-radio-button value=${value}
                    >${info.label}</sl-radio-button
                  >
                `,
              )}
            </sl-radio-group>
            <div class="hint">
              ${DATA_SHARING_LABELS[
                this.settings.dataSharing?.allowCgmDataSharing || "none"
              ].description}
            </div>
          </div>

          ${this.saved
            ? html`
                <sl-alert variant="success" open style="margin-bottom: 1rem;">
                  <sl-icon slot="icon" name="check-circle"></sl-icon>
                  Settings saved successfully
                </sl-alert>
              `
            : ""}

          <div class="actions">
            <sl-button
              variant="primary"
              size="large"
              ?loading=${this.saving}
              @click=${this._saveSettings}
            >
              Save Settings
            </sl-button>
            <sl-button
              variant="default"
              size="large"
              ?loading=${this.syncing}
              @click=${this._forceSync}
            >
              <sl-icon slot="prefix" name="arrow-repeat"></sl-icon>
              Sync Now
            </sl-button>
            <sl-button
              variant="warning"
              size="large"
              ?loading=${this.syncing}
              @click=${this._forcePushAll}
            >
              <sl-icon slot="prefix" name="cloud-upload"></sl-icon>
              Force Push All
            </sl-button>
          </div>

          ${this.syncMessage
            ? html`
                <sl-alert variant="primary" open style="margin-top: 1rem;">
                  <sl-icon slot="icon" name="info-circle"></sl-icon>
                  ${this.syncMessage}
                </sl-alert>
              `
            : ""}

          <div class="danger-zone">
            <h2>Danger Zone</h2>
            <div class="danger-zone-card">
              <h3>Delete All Data</h3>
              <p>
                Permanently delete all your data from this device and the
                server. This action cannot be undone. Your account will remain
                but all log entries, settings, and CGM data will be erased.
              </p>
              <sl-button
                variant="danger"
                outline
                @click=${this._openDeleteDialog}
              >
                <sl-icon slot="prefix" name="trash"></sl-icon>
                Delete All My Data
              </sl-button>
            </div>
          </div>

          <sl-dialog
            label="Delete All Data"
            ?open=${this.showDeleteDialog}
            @sl-request-close=${this._closeDeleteDialog}
          >
            <sl-alert variant="danger" open>
              <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
              <strong>Warning:</strong> This will permanently delete all your
              data. This cannot be undone.
            </sl-alert>

            <p style="margin-top: 1rem;">
              All your log entries, CGM readings, settings, and synchronised
              data will be permanently deleted from both this device and the
              server.
            </p>

            <div class="delete-confirm-input">
              <label>
                To confirm, type <code>DELETE ALL MY DATA</code> below:
              </label>
              <sl-input
                placeholder="Type confirmation phrase"
                value=${this.deleteConfirmText}
                @sl-input=${(e: Event) =>
                  (this.deleteConfirmText = (
                    e.target as HTMLInputElement
                  ).value)}
              ></sl-input>
            </div>

            ${this.deleteError
              ? html`
                  <sl-alert variant="danger" open style="margin-top: 1rem;">
                    ${this.deleteError}
                  </sl-alert>
                `
              : ""}

            <div
              slot="footer"
              style="display: flex; gap: 0.5rem; justify-content: flex-end;"
            >
              <sl-button @click=${this._closeDeleteDialog}>Cancel</sl-button>
              <sl-button
                variant="danger"
                ?loading=${this.deleting}
                @click=${this._confirmDeleteAllData}
              >
                Delete All Data
              </sl-button>
            </div>
          </sl-dialog>
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "settings-page": SettingsPage;
  }
}
